import * as React from 'react';
import {
  makeStyles,
  shorthands,
  tokens,
  Skeleton,
  SkeletonItem,
} from '@fluentui/react-components';

/**
 * Loading-state skeletons for the order detail page. Replaces the bare
 * centered spinners so the page paints its real shape immediately and the
 * delay (the heavy Booqable include) reads as "filling in" rather than a
 * blank screen. Two exports:
 *
 *   - OrderDetailsSkeleton — the whole page (header + main column + right
 *     sidebar), shown while the first local-mirror fetch is in flight.
 *   - ProductsSkeleton     — just the products table + totals, shown inside
 *     the Products section while the Booqable lines/totals load.
 */

const useStyles = makeStyles({
  page: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('16px'),
    ...shorthands.padding('16px', '24px'),
    width: '100%',
    boxSizing: 'border-box',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    ...shorthands.gap('12px'),
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('12px'),
  },
  headerActions: {
    display: 'flex',
    ...shorthands.gap('8px'),
  },
  body: {
    display: 'flex',
    ...shorthands.gap('16px'),
    alignItems: 'flex-start',
  },
  main: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('16px'),
    flexGrow: 1,
    minWidth: 0,
  },
  sidebar: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('16px'),
    width: '320px',
    flexShrink: 0,
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('12px'),
    ...shorthands.padding('16px'),
    ...shorthands.borderRadius('4px'),
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow4,
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('12px'),
  },
  // Fixed pixel widths for individual skeleton bars. SkeletonItem stretches to
  // fill its track by default, so a width wrapper gives each bar a believable
  // proportion (a title is wider than a label, etc).
  w120: { width: '120px' },
  w160: { width: '160px' },
  w80: { width: '80px' },
  w40: { width: '40px' },
  grow: { flexGrow: 1 },
  fullWidth: { width: '100%' },
  productRows: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('12px'),
  },
  totalsRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    ...shorthands.gap('12px'),
    marginTop: '8px',
  },
});

const CardSkeleton: React.FC<{ rows?: number }> = ({ rows = 3 }) => {
  const styles = useStyles();
  return (
    <Skeleton aria-label="Loading" className={styles.card}>
      <div className={styles.w160}>
        <SkeletonItem size={16} />
      </div>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className={styles.row}>
          <div className={styles.w120}>
            <SkeletonItem size={12} />
          </div>
          <div className={styles.grow}>
            <SkeletonItem size={12} />
          </div>
        </div>
      ))}
    </Skeleton>
  );
};

/**
 * Products table skeleton — a header bar, a handful of line rows, and a
 * totals block, matching the Products section's real layout closely enough
 * that there's no jump when the rows land.
 */
export const ProductsSkeleton: React.FC<{ rows?: number }> = ({ rows = 4 }) => {
  const styles = useStyles();
  return (
    <Skeleton aria-label="Loading items" className={styles.fullWidth}>
      <div className={styles.productRows}>
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className={styles.row}>
            <div className={styles.w40}>
              <SkeletonItem shape="square" size={32} />
            </div>
            <div className={styles.grow}>
              <SkeletonItem size={12} />
            </div>
            <div className={styles.w80}>
              <SkeletonItem size={12} />
            </div>
            <div className={styles.w80}>
              <SkeletonItem size={12} />
            </div>
          </div>
        ))}
        <div className={styles.totalsRow}>
          <div className={styles.w120}>
            <SkeletonItem size={16} />
          </div>
        </div>
      </div>
    </Skeleton>
  );
};

/**
 * Full-page order detail skeleton. Shown only during the initial local-mirror
 * fetch (before progressive render flips loadingOrder off), so it spends a
 * short moment on screen — but it stops the page from blanking to a single
 * centered spinner.
 */
export const OrderDetailsSkeleton: React.FC = () => {
  const styles = useStyles();
  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.w160}>
            <SkeletonItem size={24} />
          </div>
          <div className={styles.w80}>
            <SkeletonItem size={20} />
          </div>
        </div>
        <div className={styles.headerActions}>
          <Skeleton aria-label="Loading">
            <div className={styles.w120}>
              <SkeletonItem size={32} />
            </div>
          </Skeleton>
        </div>
      </div>
      <div className={styles.body}>
        <div className={styles.main}>
          <CardSkeleton rows={2} />
          <CardSkeleton rows={4} />
          <CardSkeleton rows={3} />
        </div>
        <div className={styles.sidebar}>
          <CardSkeleton rows={3} />
          <CardSkeleton rows={2} />
        </div>
      </div>
    </div>
  );
};

export default OrderDetailsSkeleton;
